<template>
  <div class="min-h-screen bg-gray-100 flex flex-col justify-center items-center py-12 sm:px-6 lg:px-8">
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <div class="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
        <div v-if="isLoading">
          <h2 class="text-2xl font-semibold text-gray-900 mb-4">
            {{ i18n.t('message.vkCallbackProcessingTitle', 'Processing VK Login...') }}
          </h2>
          <p class="text-sm text-gray-600 mb-6">
            {{ i18n.t('message.vkCallbackProcessingDesc', 'Please wait while we securely log you in with VK.') }}
          </p>
          <svg class="animate-spin h-8 w-8 text-indigo-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>

        <div v-if="error">
          <h2 class="text-2xl font-semibold text-red-700 mb-4">
            {{ i18n.t('message.vkCallbackErrorTitle', 'VK Login Failed') }}
          </h2>
          <p class="text-sm text-red-600 mb-6">{{ error }}</p>
          <router-link
            to="/register"
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {{ i18n.t('message.tryAgain', 'Try Again') }}
          </router-link>
           <router-link
            to="/signin"
            class="mt-3 w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {{ i18n.t('message.goToSignIn', 'Go to Sign In') }}
          </router-link>
        </div>

        <div v-if="!isLoading && !error && successMessage">
           <h2 class="text-2xl font-semibold text-green-700 mb-4">
            {{ i18n.t('message.vkCallbackSuccessTitle', 'Login Successful!') }}
          </h2>
          <p class="text-sm text-gray-600 mb-6">
            {{ successMessage }} {{ i18n.t('message.redirecting', 'Redirecting...') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import * as VKID from '@vkid/sdk';
import axiosInstance from '@/axios/axios'; // Ensure this path is correct for your project
import { useAuthStore } from '@/stores/user';   // Ensure this path is correct for your project
import { useI18n } from 'vue-i18n';

const i18n = useI18n();
const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const isLoading = ref(true);
const error = ref(null);
const successMessage = ref(null);

const VITE_VK_CLIENT_ID = import.meta.env.VITE_VK_CLIENT_ID;

const processVkLoginWithBackend = async (vkAuthData) => {
  const tokenForBackend = vkAuthData.access_token || vkAuthData.token;
  const vkUser = vkAuthData.user || {
    id: vkAuthData.user_id,
    email: vkAuthData.email,
    first_name: vkAuthData.first_name,
    last_name: vkAuthData.last_name
  };

  if (!tokenForBackend) {
    error.value = i18n.t('message.vkTokenError', 'VK authentication successful, but token missing.');
    isLoading.value = false;
    return;
  }

  try {
    const response = await axiosInstance.post('/api/auth/vk-login/', {
      access_token: tokenForBackend,
      email: vkUser.email,
      vk_user_id: vkUser.id,
      first_name: vkUser.first_name,
      last_name: vkUser.last_name,
    });

    if (response.data && response.data.access_token && response.data.user) {
      authStore.setTokens(response.data.access_token, response.data.refresh_token);
      authStore.setUser(response.data.user);
      successMessage.value = i18n.t('message.vkLoginSuccess', 'Successfully logged in with VK.');
      setTimeout(() => {
        router.push('/app');
      }, 1500);
    } else {
      error.value = i18n.t('message.vkLoginBackendError', 'Login after VK auth failed on our server.');
    }
  } catch (err) {
    console.error('Error during backend VK login processing:', err);
    const errorMessage = err.response?.data?.detail || err.response?.data?.message || i18n.t('message.vkLoginError', 'An error occurred with VK login.');
    error.value = errorMessage;
  } finally {
    isLoading.value = false;
  }
};

const handleVkSdkGlobalError = (sdkError) => {
  console.error('VK SDK Error on callback page:', sdkError);
  let errorMessage = i18n.t('message.vkGenericError', 'VK login failed.');
  if (sdkError) {
    if (typeof sdkError === 'string') errorMessage = sdkError;
    else if (sdkError.description) errorMessage = `VK: ${sdkError.description}`;
    else if (sdkError.error_description) errorMessage = `VK: ${sdkError.error_description}`;
    else if (sdkError.message) errorMessage = `VK: ${sdkError.message}`;
    else if (sdkError.type) errorMessage = `VK Error: ${sdkError.type}`;
  }
  error.value = errorMessage;
  isLoading.value = false;
};

onMounted(async () => {
  isLoading.value = true;
  error.value = null;
  successMessage.value = null;

  const { code, device_id, state: returnedState } = route.query;

  if (!code || !device_id) {
    error.value = i18n.t('message.vkCallbackInvalidParams', 'Invalid callback parameters from VK.');
    isLoading.value = false;
    return;
  }

  // Note: For production, 'state' should be dynamically generated, stored (e.g., sessionStorage), and verified here.
  // The 'codeVerifier' should also be dynamic if PKCE is fully implemented.
  // This example uses static values as per the existing Register.vue.
  if (typeof VKID !== 'undefined' && VITE_VK_CLIENT_ID) {
    try {
      VKID.Config.init({
        app: parseInt(VITE_VK_CLIENT_ID, 10),
        redirectUrl: `${window.location.origin}/oauth/vk/callback`,
        state: 'state', // Should match state from initial auth request
        codeVerifier: 'codeVerifier', // Should match verifier for PKCE
      });
    } catch (configError) {
      console.warn("Warning: VKID.Config.init on callback page:", configError.message);
    }
  } else {
    error.value = i18n.t('message.vkSdkConfigErrorCallback', 'VK SDK or configuration is missing.');
    isLoading.value = false;
    return;
  }

  try {
    const vkAuthData = await VKID.Auth.exchangeCode(String(code), String(device_id));
    await processVkLoginWithBackend(vkAuthData);
  } catch (exchangeError) {
    handleVkSdkGlobalError(exchangeError);
  }
});
</script>
